import { Form, Steps, Row, Col, Upload, App, Alert } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import InputWithButton from "../../../components/CustomInput/InputWithButton";
import CustomButton from "../../../components/CustomButton/Button";
import ReferenceLinksFormList from "../../../components/ReferenceLinksFormList/ReferenceLinksFormList";
import { formatReferenceLinks } from "../../../helpers/referenceLinks";
import { ERROR_MSGS, DESC_TXT } from "../../../constants/constants";
import ErrorAlert from "../../../components/ErrorAlert/ErrorAlert";
import { useDispatch, useSelector } from "react-redux";
import {
  companyNameRules,
  nameRules,
  emailRules,
  phoneNumberRules,
  panRules,
  capitalizeWords,
  tenureRules,
  scTenureRules,
  websiteRules,
  validateWebsiteUrl,
  gstinRules,
} from "../../../helpers/helpers";

import {
  addNewEnquiry,
  getDropdownData,
  getRmCities,
  updateErrorStatus,
  getEnquiryManagementView,
  getBusByPan,
} from "../../../redux/slices/EnqMngtSlice";

import {
  newEnquirySendOtp,
  newEnquiryValidateOtp,
  updateOtpErrorStatus,
} from "../../../redux/slices/BusSlice";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";

import InfoModal from "../../../components/CustomModal/InfoModal";
import OtpContent from "../../../components/OtpContent/OtpContent";
import PrimaryButton from "../../../components/CustomButton/PrimaryButton";
import { handleUploadFile } from "../../../helpers/helpers";
import {
  PRODUCT_GROUPS,
  getFinancingSubcategoryOptions,
  getProductGroupOptions,
  isEquityCategory,
  isInvoiceStyleCategory,
  isVenturesCategory,
  resolveDealCategoryIdForProductGroup,
} from "../../../helpers/dealCategoryProducts";

const { Step } = Steps;
const { Dragger } = Upload;

const STEP_1_FIELDS = [
  "busName",
  "contactName",
  "workEmail",
  "pocPhoneNumber",
  "busWebsite",
];

const getStepZeroFields = (productGroup, dealTypeVal) => {
  const fields = ["productGroup"];

  // Financing shows dealType; Ventures/Equity store a hidden leaf dealType on the form.
  if (
    productGroup === PRODUCT_GROUPS.FINANCING ||
    productGroup === PRODUCT_GROUPS.VENTURES ||
    productGroup === PRODUCT_GROUPS.EQUITY
  ) {
    fields.push("dealType");
  }

  if (
    productGroup === PRODUCT_GROUPS.VENTURES ||
    productGroup === PRODUCT_GROUPS.EQUITY ||
    isInvoiceStyleCategory(dealTypeVal)
  ) {
    fields.push("busPan");
  }

  if (isInvoiceStyleCategory(dealTypeVal)) {
    fields.push("gstin");
  }

  return fields;
};

const resolveLeafDealType = (productGroup, dealTypeVal) =>
  dealTypeVal || resolveDealCategoryIdForProductGroup(productGroup) || null;

const getStepTwoFields = (dealTypeVal) => {
  if (isInvoiceStyleCategory(dealTypeVal)) {
    return [
      "entName",
      "industryType",
      "busType",
      "annualTurnover",
      "tenure",
      "expAmt",
    ];
  }

  if (isVenturesCategory(dealTypeVal)) {
    return [
      "busType",
      "industryType",
      "pitchDeck",
      "amountToRaise",
      "dealDetails",
      "otherTerms",
      "otherLinks",
    ];
  }

  return [];
};

const NewEnqForm = ({
  onClose,
  onSuccess,
  selectedStatus,
  initialStep = 0,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const busPan = Form.useWatch("busPan", form);
  const dispatch = useDispatch();
  const emailValue = Form.useWatch("workEmail", form);
  const phoneValue = Form.useWatch("pocPhoneNumber", form);
  const busWebsiteValue = Form.useWatch("busWebsite", form);
  const productGroup = Form.useWatch("productGroup", form);
  const dealType = Form.useWatch("dealType", form);
  const { dropdownData, error, newEnquiryLoading } = useSelector(
    (state) => state.enqMngt,
  );
  const [otpModal, setOtpModal] = useState({
    type: null,
  });
  const [verified, setVerified] = useState({
    email: false,
    mobile: false,
  });

  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verifiedMobile, setVerifiedMobile] = useState("");

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [stepOneData, setStepOneData] = useState({});
  const [typeDisabled, setTypeDisabled] = useState(false);
  // Lock Annual Turnover only when an existing org already has a value (JV often has none)
  const [annualTurnoverLocked, setAnnualTurnoverLocked] = useState(false);

  const [panFetched, setPanFetched] = useState(false);
  const [isPanDataAvailable, setIsPanDataAvailable] = useState(false);
  const [fetchedPan, setFetchedPan] = useState("");
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(null);

  const effectiveDealType = resolveLeafDealType(
    stepOneData?.productGroup || productGroup,
    stepOneData?.dealType || dealType,
  );

  const getOtpFieldName = (type) =>
    type === "email" ? "workEmail" : "pocPhoneNumber";

  const getOtpErrorMessage = (error) =>
    error?.message || error || "Unable to send OTP. Please try again.";

  useEffect(() => {
    dispatch(getDropdownData());
    dispatch(getRmCities({ forBusiness: true }));

    return () => {
      dispatch(updateErrorStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(updateErrorStatus());
  }, [currentStep, dispatch]);

  useEffect(() => {
    dispatch(updateErrorStatus());
  }, [busWebsiteValue, dispatch]);

  // Keep leaf dealType in sync for Ventures/Equity (no subcategory dropdown).
  // Deferred so unmounting the Financing dealType field cannot clear it.
  useEffect(() => {
    if (
      productGroup !== PRODUCT_GROUPS.VENTURES &&
      productGroup !== PRODUCT_GROUPS.EQUITY
    ) {
      return;
    }

    const resolvedDealType =
      resolveDealCategoryIdForProductGroup(productGroup);
    if (!resolvedDealType) return;

    const frame = requestAnimationFrame(() => {
      if (form.getFieldValue("dealType") !== resolvedDealType) {
        form.setFieldsValue({ dealType: resolvedDealType });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [productGroup, form]);

  // SEND OTP
  const handleVerifyBtnClick = async (type) => {
    const values = form.getFieldsValue();
    const fieldName = getOtpFieldName(type);

    const payload =
      type === "email"
        ? { email: values.workEmail }
        : { mobile: values.pocPhoneNumber };

    dispatch(updateOtpErrorStatus());
    setVerifyOtpLoading(type);

    try {
      const response = await dispatch(newEnquirySendOtp(payload));

      if (newEnquirySendOtp.fulfilled.match(response)) {
        form.setFields([{ name: fieldName, errors: [] }]);
        setOtpModal({ type });
        return;
      }

      form.setFields([
        {
          name: fieldName,
          errors: [getOtpErrorMessage(response.payload)],
        },
      ]);
    } finally {
      setVerifyOtpLoading(null);
    }
  };

  // RESEND OTP
  const handleResendOtp = () => {
    const values = form.getFieldsValue();

    if (otpModal.type === "email") {
      dispatch(newEnquirySendOtp({ email: values.workEmail }));
    } else {
      dispatch(newEnquirySendOtp({ mobile: values.pocPhoneNumber }));
    }
  };

  // VALIDATE OTP
  const handleValidateOtp = async (payload) => {
    const response = await dispatch(newEnquiryValidateOtp(payload));

    if (response?.payload?.statusCode === 201) {
      if (otpModal.type === "email") {
        setVerified((prev) => ({ ...prev, email: true }));
        setVerifiedEmail(form.getFieldValue("workEmail"));
      } else {
        setVerified((prev) => ({ ...prev, mobile: true }));
        setVerifiedMobile(form.getFieldValue("pocPhoneNumber"));
      }

      setOtpModal({ type: null });
    }
  };

  useEffect(() => {
    const currentEmail = form.getFieldValue("workEmail");

    if (verified.email && currentEmail !== verifiedEmail) {
      setVerified((prev) => ({
        ...prev,
        email: false,
      }));
    }

    form.setFields([{ name: "workEmail", errors: [] }]);
    dispatch(updateOtpErrorStatus());
  }, [emailValue, form, verified.email, verifiedEmail, dispatch]);

  useEffect(() => {
    const currentMobile = form.getFieldValue("pocPhoneNumber");

    if (verified.mobile && currentMobile !== verifiedMobile) {
      setVerified((prev) => ({
        ...prev,
        mobile: false,
      }));
    }

    form.setFields([{ name: "pocPhoneNumber", errors: [] }]);
    dispatch(updateOtpErrorStatus());
  }, [form, phoneValue, verified.mobile, verifiedMobile, dispatch]);

  // NEW - Get Business Details By PAN on Step 2
  useEffect(() => {
    const fetchBusDetails = async () => {
      const pan = form.getFieldValue("busPan");

      // Call API only in Step 2 and PAN available
      if (
        currentStep === 1 &&
        !panFetched &&
        pan &&
        (isInvoiceStyleCategory(stepOneData?.dealType) ||
          isVenturesCategory(stepOneData?.dealType) ||
          isEquityCategory(stepOneData?.dealType) ||
          isInvoiceStyleCategory(effectiveDealType) ||
          isVenturesCategory(effectiveDealType) ||
          isEquityCategory(effectiveDealType))
      ) {
        try {
          const res = await dispatch(getBusByPan(pan));

          const data = res?.payload?.response;

          if (data) {
            const currentValues = form.getFieldsValue(true);

            form.setFieldsValue({
              ...currentValues,
              industryType: data?.industryTypeId || currentValues.industryType,
              busType: data?.businessTypeId || currentValues.busType,
              annualTurnover: data?.annualTurnoverId || undefined,
              workEmail: data?.email || currentValues.workEmail,
              busName: data?.busName || currentValues.busName,
              contactName: data?.contactName || currentValues.contactName,
              pocPhoneNumber:
                data?.contactPhoneNumber || currentValues.pocPhoneNumber,
              busWebsite: data?.busWebsite || currentValues.busWebsite,
            });
            // ✅ Email verification status
            if (data?.kycDetails?.kycEmail?.isVerified) {
              setVerified((prev) => ({
                ...prev,
                email: true,
              }));

              setVerifiedEmail(data?.email);
            }
            // ✅ Mobile verification status
            if (data?.kycDetails?.kycMobile?.isVerified) {
              setVerified((prev) => ({
                ...prev,
                mobile: true,
              }));

              setVerifiedMobile(data?.contactPhoneNumber);
            }
            setFetchedPan(pan);
            setTypeDisabled(true);
            setAnnualTurnoverLocked(Boolean(data?.annualTurnoverId));
            setIsPanDataAvailable(true);
          } else {
            // No Record Found

            setFetchedPan("");
            setTypeDisabled(false);
            setAnnualTurnoverLocked(false);

            setVerified({
              email: false,
              mobile: false,
            });

            setVerifiedEmail("");
            setVerifiedMobile("");

            form.setFieldsValue({
              industryType: undefined,
              busType: undefined,
              annualTurnover: undefined,
              workEmail: undefined,
              busName: undefined,
              contactName: undefined,
              pocPhoneNumber: undefined,
              busWebsite: undefined,
            });
          }
          setPanFetched(true);
        } catch (err) {
          setIsPanDataAvailable(false);
          setTypeDisabled(false);
          setAnnualTurnoverLocked(false);
          message.error("Failed to fetch business details", err);

          form.setFieldsValue({
            industryType: undefined,
            busType: undefined,
            annualTurnover: undefined,
            workEmail: undefined,
            busName: undefined,
            contactName: undefined,
            pocPhoneNumber: undefined,
            busWebsite: undefined,
          });
        }
      }
    };

    fetchBusDetails();
  }, [currentStep, stepOneData, dispatch, form, panFetched, message, effectiveDealType]);

  useEffect(() => {
    if (fetchedPan && busPan && busPan !== fetchedPan) {
      setTypeDisabled(false);
      setAnnualTurnoverLocked(false);
      setPanFetched(false);

      setVerified({
        email: false,
        mobile: false,
      });

      setVerifiedEmail("");
      setVerifiedMobile("");
    }
  }, [busPan, fetchedPan]);

  const setBusWebsiteFieldError = (errorMessage) => {
    form.setFields([{ name: "busWebsite", errors: [errorMessage] }]);
  };

  const handleSubmit = async () => {
    try {
      dispatch(updateErrorStatus());

      const productGroupVal =
        form.getFieldValue("productGroup") || stepOneData?.productGroup;
      const dealTypeVal = resolveLeafDealType(
        productGroupVal,
        form.getFieldValue("dealType") || stepOneData?.dealType,
      );

      if (currentStep === 0) {
        const values = await form.validateFields(
          getStepZeroFields(productGroupVal, dealTypeVal),
        );
        setStepOneData((prev) => ({
          ...prev,
          ...values,
          productGroup: values.productGroup || productGroupVal,
          dealType: resolveLeafDealType(
            values.productGroup || productGroupVal,
            values.dealType || dealTypeVal,
          ),
        }));
        setCurrentStep(1);
        return;
      }

      if (currentStep === 1) {
        const websiteError = validateWebsiteUrl(
          form.getFieldValue("busWebsite"),
        );
        if (websiteError) {
          setBusWebsiteFieldError(websiteError);
          return;
        }

        await form.validateFields(STEP_1_FIELDS);

        const values = form.getFieldsValue([
          ...STEP_1_FIELDS,
          ...getStepZeroFields(productGroupVal, dealTypeVal),
        ]);
        setStepOneData((prev) => ({
          ...prev,
          ...values,
          productGroup: values.productGroup || productGroupVal,
          dealType: resolveLeafDealType(
            values.productGroup || productGroupVal,
            values.dealType || dealTypeVal,
          ),
        }));
        setCurrentStep(2);
        return;
      }

      const websiteError = validateWebsiteUrl(stepOneData?.busWebsite);
      if (websiteError) {
        setCurrentStep(1);
        setTimeout(() => setBusWebsiteFieldError(websiteError), 0);
        return;
      }

      const values = await form.validateFields(
        getStepTwoFields(effectiveDealType || stepOneData?.dealType),
      );

      const finalData = {
        ...stepOneData,
        ...values,
        dealType: effectiveDealType || stepOneData?.dealType,
      };

      let payload = {
        // Common fields
        isExistingBusiness: typeDisabled,
        busName: finalData.busName,
        pocName: finalData.contactName,
        email: finalData.workEmail,
        phoneNumber: finalData.pocPhoneNumber,
        pan: finalData.busPan,
        // gstin: finalData.gstin,
        website: finalData.busWebsite?.trim()
          ? `http://${finalData.busWebsite
            ?.trim()
            .replace(/^(https?:\/\/)+/gi, "")}`
          : undefined,
        dealCategoryId: finalData.dealType, // ✅ always send
      };

      // Invoice Financing / Supply Chain Financing
      if (isInvoiceStyleCategory(finalData.dealType)) {
        payload = {
          ...payload,
          // pan: finalData.busPan,
          gstin: finalData.gstin,
          entName: values.entName,
          businessTypeId: values.busType,
          industryTypeId: values.industryType,
          annualTurnoverId: values.annualTurnover,
          expectedCredit: values.expAmt,
          expectedTenure: values.tenure,
        };
      }

      // Ventures
      if (isVenturesCategory(finalData.dealType)) {
        const fileData = finalData.pitchDeck?.[0];
        const pitchDeckUrl =
          fileData?.url || fileData?.response?.fileUrl || "";

        // Read optional fields from form directly so Form.List values are not dropped
        const otherLinks =
          values.otherLinks ?? form.getFieldValue("otherLinks");
        const otherTerms =
          values.otherTerms ?? form.getFieldValue("otherTerms");

        payload = {
          ...payload,
          businessTypeId: values.busType,
          industryTypeId: values.industryType,
          amountToRaise: values.amountToRaise,
          dealDetails: values.dealDetails,
          otherTermsConditions: otherTerms || undefined,
          referenceLinks: formatReferenceLinks(otherLinks),
          pitchDeck: pitchDeckUrl, // ✅ uploaded file URL
        };
      }

      let res = await dispatch(addNewEnquiry(payload));

      if (res?.payload?.statusCode === 201) {
        dispatch(getEnquiryManagementView(selectedStatus));
        dispatch(count("ENQUIRYMGMT"));
        dispatch(allCount("ALL"));

        form.resetFields();
        setStepOneData({});
        setCurrentStep(0);

        onSuccess?.();
      }
    } catch {
      // validation failed
    }
  };

  return (
    <>
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="Deal Information" />
        <Step title="Basic Information" />
        <Step title="Deals Details" />
      </Steps>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{ remember: true }}
      >
        {/* STEP 1 */}
        {currentStep === 0 && (
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Deal Category"
                name="productGroup"
                showSearch
                isRequired={true}
                placeholder="Select Deal Category"
                options={getProductGroupOptions()}
                onChange={(value) => {
                  const resolvedDealType =
                    resolveDealCategoryIdForProductGroup(value);
                  form.setFieldsValue({
                    dealType: resolvedDealType || undefined,
                    busPan: undefined,
                    gstin: undefined,
                  });
                }}
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.SEL_DEAL_TYPE_ERR_MSG,
                  },
                ]}
              />
            </Col>

            {productGroup === PRODUCT_GROUPS.FINANCING && (
              <Col xs={24} md={12}>
                <CustomSelect
                  label="Financing Category"
                  name="dealType"
                  showSearch
                  isRequired={true}
                  placeholder="Select Financing Category"
                  options={getFinancingSubcategoryOptions(
                    dropdownData?.category,
                  )}
                  onChange={() => {
                    form.setFieldsValue({
                      busPan: undefined,
                      gstin: undefined,
                    });
                  }}
                  rules={[
                    {
                      required: true,
                      message: ERROR_MSGS.SEL_DEAL_TYPE_ERR_MSG,
                    },
                  ]}
                />
              </Col>
            )}

            {(productGroup === PRODUCT_GROUPS.VENTURES ||
              productGroup === PRODUCT_GROUPS.EQUITY ||
              isInvoiceStyleCategory(dealType)) && (
              <Col xs={24} md={12}>
                <CustomInput
                  label="Business PAN"
                  name="busPan"
                  type="text"
                  isRequired={true}
                  placeholder="Enter your Business PAN"
                  rules={panRules}
                  isUpperCase={true}
                />
              </Col>
            )}

            {isInvoiceStyleCategory(dealType) && (
              <Col xs={24} md={12}>
                <CustomInput
                  label="Company GSTIN"
                  name="gstin"
                  type="text"
                  isRequired={true}
                  placeholder="Enter your Company GSTIN"
                  rules={gstinRules}
                  isUpperCase={true}
                />
              </Col>
            )}
          </Row>
        )}
        {/* STEP 1 */}
        {/* STEP 2 */}
        {currentStep === 1 && (
          <Row gutter={[24, 0]}>
            <Col xs={24}>
              <Alert
                message={DESC_TXT.POC_DIRECTOR_NOTE}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Business Name (as per PAN)"
                name="busName"
                type="text"
                isRequired={true}
                disabled={typeDisabled}
                placeholder="Enter your Business Name"
                rules={companyNameRules("Business")}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Business Contact Name"
                name="contactName"
                type="text"
                isRequired={true}
                disabled={typeDisabled}
                placeholder="Enter Business Contact Name"
                rules={nameRules("Business Contact")}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputWithButton
                label="Work Email"
                name="workEmail"
                type="email"
                isRequired={true}
                placeholder="Enter your Business email address"
                rules={emailRules("Work")}
                verifyBtn={true}
                disabled={typeDisabled}
                isVerified={verified.email}
                disableVerify={
                  verified.email ||
                  !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(emailValue || "")
                }
                verifyLoading={verifyOtpLoading === "email"}
                handleVerify={() => handleVerifyBtnClick("email")}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputWithButton
                label="Contact Phone Number (Should be linked with Aadhaar)"
                name="pocPhoneNumber"
                placeholder="Phone Number"
                rules={phoneNumberRules("Point of Contact")}
                isRequired={true}
                verifyBtn={true}
                prefix="+91"
                disabled={typeDisabled}
                isVerified={verified.mobile}
                disableVerify={
                  verified.mobile || !/^[6-9]\d{9}$/.test(phoneValue || "")
                }
                verifyLoading={verifyOtpLoading === "phoneNumber"}
                handleVerify={() => handleVerifyBtnClick("phoneNumber")}
              />
            </Col>

            <Col xs={24} md={12}>
              <CustomInput
                label="Business Website"
                name="busWebsite"
                type="text"
                // isRequired={true}
                disabled={typeDisabled}
                // prefix={<span>https://</span>}
                placeholder="Enter Business Website"
                rules={websiteRules}
                removeHttps={true}
              />
            </Col>
          </Row>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && isInvoiceStyleCategory(effectiveDealType) && (
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Industry Type"
                name="industryType"
                isRequired={true}
                showSearch
                disabled={typeDisabled}
                placeholder="Select Industry Type"
                options={dropdownData?.industryTypeList?.map((industry) => ({
                  value: industry.industryTypeId,
                  label: capitalizeWords(industry.industryTypeName),
                }))}
                rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Business Type"
                name="busType"
                isRequired={true}
                disabled={typeDisabled}
                placeholder="Select Business Type"
                options={dropdownData?.businessTypeList?.map(
                  (businessType) => ({
                    value: businessType.businessTypeId,
                    label: capitalizeWords(businessType.businessTypeName),
                  }),
                )}
                rules={[{ required: true, message: ERROR_MSGS.SEL_COMP_TYPE }]}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Annual Turnover"
                name="annualTurnover"
                isRequired={true}
                disabled={annualTurnoverLocked}
                placeholder="Select Annual Turnover"
                options={dropdownData?.annualTurnoverList?.map(
                  (annualTurnover) => ({
                    value: annualTurnover.annualTurnoverId,
                    label: capitalizeWords(annualTurnover.annualTurnoverName),
                  }),
                )}
                rules={[{ required: true, message: ERROR_MSGS.SEL_ANN_TURN }]}
              />
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.dealType !== currentValues.dealType}
                >
                  {() => {
                    const isSupplyChain = form.getFieldValue("dealType") === "DCT1000006";
                    return (
                      <CustomInput
                        label="Credit Tenure"
                        name="tenure"
                        isRequired={true}
                        type="text"
                        placeholder={isSupplyChain ? "Enter your Credit Tenure (Between 30 and 120 Days)" : "Enter your Credit Tenure (Between 30 and 90 Days)"}
                        rules={isSupplyChain ? scTenureRules : tenureRules}
                      />
                    );
                  }}
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <CustomInputNumber
                label="Expected Amount"
                name="expAmt"
                controls={false}
                isRequired={true}
                placeholder="How much working capital do you need?"
                rules={[
                  {
                    required: true,
                    message: ERROR_MSGS.EXP_AMT_ERR_MSG,
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value >= 200000) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "Expected Amount minimum 2 lakhs should be required!",
                        ),
                      );
                    },
                  },
                ]}
              />
            </Col>
            <Col xs={24} md={12}>
              <CustomInput
                label="Enterprise Name"
                name="entName"
                type="text"
                isRequired={true}
                placeholder="Enter your Enterprise Name"
                rules={companyNameRules("Enterprise")}
              />
            </Col>
          </Row>
        )}

        {currentStep === 2 && isVenturesCategory(effectiveDealType) && (
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <CustomSelect
                label="Business Type"
                name="busType"
                isRequired={true}
                placeholder="Select Business Type"
                disabled={typeDisabled}
                options={dropdownData?.businessTypeList?.map(
                  (businessType) => ({
                    value: businessType.businessTypeId,
                    label: capitalizeWords(businessType.businessTypeName),
                  }),
                )}
                rules={[{ required: true, message: ERROR_MSGS.SEL_COMP_TYPE }]}
              />
            </Col>

            <Col xs={24} md={12}>
              <CustomSelect
                label="Industry Type"
                name="industryType"
                showSearch
                isRequired={true}
                placeholder="Select Industry Type"
                disabled={typeDisabled}
                options={dropdownData?.industryTypeList?.map((industry) => ({
                  value: industry.industryTypeId,
                  label: capitalizeWords(industry.industryTypeName),
                }))}
                rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
              />
            </Col>

            <Col xs={24}>
              <Form.Item
                label="Upload Your Proposal"
                name="pitchDeck"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) return e;

                  return e?.fileList?.map((file) => {
                    if (file.response) {
                      return {
                        ...file,
                        url: file.response.fileUrl, // 👈 IMPORTANT
                      };
                    }
                    return file;
                  });
                }}
                rules={[
                  { required: true, message: "Please Upload Your Proposal" },
                ]}
              >
                <Dragger
                  customRequest={async ({
                    file,
                    onSuccess,
                    onError,
                    onProgress,
                  }) => {
                    try {
                      const { fileUrl, fileName } = await handleUploadFile(
                        file,
                        "pitchDeck",
                        (percent) => {
                          onProgress({ percent });
                        },
                      );

                      onSuccess({
                        fileUrl: encodeURI(fileUrl), // 👈 THIS is important
                        fileName,
                      });
                    } catch (err) {
                      onError(err);
                    }
                  }}
                  maxCount={1}
                  accept=".pdf,.ppt,.pptx"
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">Upload Your Proposal</p>
                  <p className="ant-upload-hint">
                    Accepted: PDF, PPT, PPTX | Max: 10MB
                  </p>
                </Dragger>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <CustomInputNumber
                label="Amount to Raise (₹)"
                name="amountToRaise"
                isRequired={true}
                placeholder="Enter amount"
                controls={false}
                rules={[
                  {
                    required: true,
                    message: "Amount to Raise cannot be empty!",
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value >= 200000) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          "Minimum amount to raise should be ₹2,00,000!",
                        ),
                      );
                    },
                  },
                ]}
              />
            </Col>

            <Col xs={24}>
              <CustomInput
                label="Deal Details / Expectations"
                name="dealDetails"
                rows={4}
                isTextArea={true}
                isRequired={true}
                placeholder="Describe what you're looking for..."
                rules={[
                  { required: true, message: "Deal Details cannot be empty!" },
                ]}
              />
            </Col>

            <Col xs={24}>
              <CustomInput
                label="Other Terms (Optional)"
                name="otherTerms"
                rows={4}
                isTextArea={true}
                placeholder="Any other terms or conditions..."
              />
            </Col>

            <Col xs={24}>
              <ReferenceLinksFormList />
            </Col>
          </Row>
        )}

        {error && <ErrorAlert error={error} />}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px",
            padding: "20px",
          }}
        >
          {/* Step 2 → Previous CustomButton */}
          {(currentStep === 1 || currentStep === 2) && (
            <CustomButton
              onClick={() => {
                if (currentStep === 1) {
                  // setPanFetched(false);
                }
                setCurrentStep((prev) => prev - 1);
                if (isPanDataAvailable) {
                  setTypeDisabled(true);
                } else {
                  // setTypeDisabled(false);
                }
              }}
              style={{
                padding: "24px",
              }}
            >
              Previous
            </CustomButton>
          )}

          {/* Step 1 → Next | Step 2 → Submit */}
          <PrimaryButton
            loading={newEnquiryLoading}
            disabled={
              currentStep === 1 && (!verified.email || !verified.mobile)
            }
            onClick={() => {
              if (currentStep === 0 || currentStep === 1) {
                handleSubmit();
              } else {
                form.submit();
              }
            }}
          >
            {currentStep === 0 || currentStep === 1 ? "Next" : "Submit"}
          </PrimaryButton>
        </div>

        {otpModal.type && (
          <InfoModal
            title={
              otpModal.type === "email"
                ? "Verify Email OTP"
                : "Verify Mobile OTP"
            }
            open
            centered
            onCancel={() => setOtpModal({ type: null })}
            content={
              <OtpContent
                validateOtp={(payload) => handleValidateOtp(payload)}
                resendOtp={handleResendOtp}
                otpType={otpModal.type}
                email={form.getFieldValue("workEmail")}
                mobile={form.getFieldValue("pocPhoneNumber")}
              />
            }
          />
        )}
      </Form>
    </>
  );
};

export default NewEnqForm;
